/* ==========================================================================
   whero-flavor-urgency.js
   --------------------------------------------------------------------------
   Re-renders flavor urgency (card pills + strips) and toggles the PDP
   availability panel's "Closest to you" column on whero:status-updated.

   Inputs: each [data-flavor-urgency] and [data-flavor-availability] element
   carries a data-shop-states attribute with a JSON map of per-shop state
   objects keyed by shop slug. The event detail's closestShop.id lets us
   find the corresponding slug.

   Re-ranking rule (matches server logic, adds proximity tie-break):
     urgency rank: last_tub (2) > leaving_soon (1) > healthy (0)
     ties: closest shop wins; else first in iteration order.
   ========================================================================== */
(function () {
  'use strict';

  // Several sections include this asset; only the first copy should
  // attach the document-level listener.
  if (window.__wheroFlavorUrgencyInit) return;
  window.__wheroFlavorUrgencyInit = true;

  var RANK = { last_tub: 2, leaving_soon: 1, healthy: 0 };

  // When a surface with a shop picker (current-flavors page) tells us
  // the visitor's explicit choice via whero:shop-selected, that shop's
  // own state drives every card: healthy/unavailable → hidden, urgent →
  // that shop's copy with no "at {shop}" qualifier. Explicit selection
  // outranks proximity re-ranking from whero:status-updated.
  var selectedSlug = null;

  // Inline copies of snippets/whero-flavor-urgency-icon.liquid — the
  // glyph must follow the state once the JS re-renders it (clock =
  // leaving_soon, flame = last_tub).
  var SVG_ATTRS = 'class="flavor-urgency__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"';
  var ICON_FLAME = '<svg ' + SVG_ATTRS + '><path d="M12 2v4"/><path d="m4.93 4.93 2.83 2.83"/><path d="M2 12h4"/><path d="M12 22a8 8 0 0 0 8-8c0-4-3-7-6-8l-2 3-2-3c-3 1-6 4-6 8a8 8 0 0 0 8 8Z"/></svg>';
  var ICON_CLOCK = '<svg ' + SVG_ATTRS + '><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';

  function safeParse(s) {
    if (!s) return null;
    try { return JSON.parse(s); } catch (_) { return null; }
  }

  function shopStatesOn(el) {
    return safeParse(el.getAttribute('data-shop-states'));
  }

  function findSlugByShopId(shopStates, shopId) {
    if (!shopStates || !shopId) return null;
    for (var slug in shopStates) {
      if (Object.prototype.hasOwnProperty.call(shopStates, slug)
          && shopStates[slug] && shopStates[slug].id === shopId) {
        return slug;
      }
    }
    return null;
  }

  function pickPrimary(shopStates, closestSlug) {
    // Returns { primary, other } entries or null if no urgency anywhere.
    var entries = [];
    for (var slug in shopStates) {
      if (Object.prototype.hasOwnProperty.call(shopStates, slug)) {
        entries.push(shopStates[slug]);
      }
    }
    var anyUrgent = entries.some(function (e) { return e.state !== 'healthy'; });
    if (!anyUrgent) return null;

    entries.sort(function (a, b) {
      var ra = RANK[a.state] || 0;
      var rb = RANK[b.state] || 0;
      if (ra !== rb) return rb - ra; // descending urgency
      // tie → closest wins, then iteration order (stable)
      if (closestSlug && a.slug === closestSlug) return -1;
      if (closestSlug && b.slug === closestSlug) return 1;
      return 0;
    });

    return { primary: entries[0], other: entries[1] || null };
  }

  function formatDate(iso) {
    if (!iso) return '';
    // iso = "YYYY-MM-DD"
    var parts = iso.split('-');
    if (parts.length !== 3) return '';
    var m = parseInt(parts[1], 10);
    var d = parseInt(parts[2], 10);
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    if (isNaN(m) || isNaN(d) || m < 1 || m > 12) return '';
    return months[m - 1] + ' ' + d;
  }

  function copyFor(primary, other) {
    // Mirrors server Liquid in snippets/whero-flavor-urgency.liquid.
    var out = { primary: '', secondary: '' };
    if (!primary) return out;

    if (primary.state === 'last_tub' && other && other.state === 'last_tub') {
      out.primary = 'Last tub at both shops — going fast.';
      return out;
    }
    if (primary.state === 'last_tub') {
      out.primary = 'Last tub at ' + primary.name + '.';
      if (other && other.state !== 'healthy') {
        if (other.tubsRemaining != null && other.tubsRemaining <= 2) {
          out.secondary = other.tubsRemaining + ' left at ' + other.name + '.';
        } else if (other.rotationEndDate) {
          out.secondary = 'Available through ' + formatDate(other.rotationEndDate) + ' at ' + other.name + '.';
        }
      }
      return out;
    }
    if (primary.state === 'leaving_soon' && other && other.state === 'leaving_soon') {
      if (primary.daysRemaining === other.daysRemaining) {
        out.primary = 'Leaving in ~' + primary.daysRemaining + ' days.';
      } else {
        var lo = Math.min(primary.daysRemaining, other.daysRemaining);
        var hi = Math.max(primary.daysRemaining, other.daysRemaining);
        out.primary = 'Leaving in ~' + lo + '–' + hi + ' days.';
      }
      return out;
    }
    if (primary.state === 'leaving_soon') {
      out.primary = 'Leaving in ~' + primary.daysRemaining + ' days at ' + primary.name + '.';
      return out;
    }
    return out;
  }

  /* Copy for a single explicitly-selected shop — no "at {shop}"
     qualifier, since the whole page is already scoped to that shop. */
  function copyForShop(entry) {
    var out = { primary: '', secondary: '' };
    if (entry.state === 'last_tub') {
      out.primary = 'Last tub — going fast.';
      if (entry.tubsRemaining != null && entry.tubsRemaining > 0 && entry.tubsRemaining <= 2) {
        out.secondary = entry.tubsRemaining + ' left.';
      } else if (entry.rotationEndDate) {
        out.secondary = 'Available through ' + formatDate(entry.rotationEndDate) + '.';
      }
    } else if (entry.state === 'leaving_soon') {
      out.primary = 'Leaving in ~' + entry.daysRemaining + ' day' + (entry.daysRemaining === 1 ? '' : 's') + '.';
    }
    return out;
  }

  function pillOf(el) {
    return el.matches('[data-role="pill"]') ? el : el.querySelector('[data-role="pill"]');
  }

  function stripOf(el) {
    return el.matches('[data-role="strip"]') ? el : el.querySelector('[data-role="strip"]');
  }

  function setStripIcon(strip, state) {
    var icon = strip.querySelector('.flavor-urgency__icon');
    if (!icon) return;
    var isClock = !!icon.querySelector('circle');
    var wantClock = state !== 'last_tub';
    if (isClock === wantClock) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = wantClock ? ICON_CLOCK : ICON_FLAME;
    icon.replaceWith(tmp.firstChild);
  }

  /* Show the pill/strip inside `el` with the given state + copy. */
  function renderUrgency(el, state, copy) {
    var pill = pillOf(el);
    if (pill) {
      pill.hidden = false;
      pill.classList.remove('pill-last-tub', 'pill-leaving');
      pill.classList.add(state === 'last_tub' ? 'pill-last-tub' : 'pill-leaving');
      var dot = pill.querySelector('.d');
      if (state === 'last_tub') {
        if (!dot) {
          var d = document.createElement('span');
          d.className = 'd';
          d.setAttribute('aria-hidden', 'true');
          pill.insertBefore(d, pill.firstChild);
        }
      } else if (dot) {
        dot.remove();
      }
      var labelEl = pill.querySelector('[data-role="pill-label"]');
      if (labelEl) labelEl.textContent = state === 'last_tub' ? 'Last tub' : 'Leaving soon';
    }

    var strip = stripOf(el);
    if (strip) {
      strip.hidden = false;
      strip.classList.toggle('is-last-tub', state === 'last_tub');
      setStripIcon(strip, state);
      var primaryLine = strip.querySelector('[data-role="primary-line"]');
      var secondaryLine = strip.querySelector('[data-role="secondary-line"]');
      if (primaryLine) primaryLine.textContent = copy.primary;
      if (secondaryLine) {
        if (copy.secondary) {
          secondaryLine.textContent = copy.secondary;
          secondaryLine.removeAttribute('hidden');
        } else {
          secondaryLine.textContent = '';
          secondaryLine.setAttribute('hidden', '');
        }
      }
    }

    el.setAttribute('data-primary-state', state);
  }

  function hideUrgency(el) {
    var pill = pillOf(el);
    if (pill) pill.hidden = true;
    var strip = stripOf(el);
    if (strip) strip.hidden = true;
    el.setAttribute('data-primary-state', 'healthy');
  }

  function mirrorReserveCtas(el, slug) {
    var flavorId = el.getAttribute('data-flavor-id');
    if (!flavorId) return;
    var reserveCtas = document.querySelectorAll('[data-reserve][data-flavor-id="' + CSS.escape(flavorId) + '"]');
    for (var i = 0; i < reserveCtas.length; i++) {
      reserveCtas[i].setAttribute('data-toast-shop', slug);
    }
  }

  /* Scope `el` to one shop's own state (explicit picker selection or a
     fixed-shop surface like the location pages). */
  function applySelectedToEl(el, slug) {
    var shopStates = shopStatesOn(el);
    if (!shopStates) return;
    var entry = shopStates[slug];
    if (!entry) {
      // Unknown slug for this card — fall back to cross-shop ranking.
      applyToUrgencyEl(el, null);
      return;
    }
    if (entry.available === false || entry.state === 'healthy') {
      hideUrgency(el);
      return;
    }
    renderUrgency(el, entry.state, copyForShop(entry));
    el.setAttribute('data-primary-shop', slug);
    mirrorReserveCtas(el, slug);
  }

  function applyToUrgencyEl(el, closestSlug) {
    var shopStates = shopStatesOn(el);
    if (!shopStates) return;
    var picked = pickPrimary(shopStates, closestSlug);
    if (!picked) return; // nothing urgent — leave server render intact

    renderUrgency(el, picked.primary.state, copyFor(picked.primary, picked.other));
    el.setAttribute('data-primary-shop', picked.primary.slug);
    mirrorReserveCtas(el, picked.primary.slug);
  }

  function applyToPdpPanel(panelEl, closestShopId) {
    if (!closestShopId) return;
    var columns = panelEl.querySelectorAll('.pdp-avail-col');
    for (var i = 0; i < columns.length; i++) {
      var col = columns[i];
      var colShopId = col.getAttribute('data-shop-id');
      col.classList.toggle('is-closest', colShopId === closestShopId);
    }
  }

  function urgencyScopeSlug(el) {
    // A fixed-shop surface (e.g. a location page) scopes its cards with
    // a data-urgency-shop ancestor; the picker's selection wins over it.
    if (selectedSlug) return selectedSlug;
    var scope = el.closest && el.closest('[data-urgency-shop]');
    return scope ? scope.getAttribute('data-urgency-shop') : null;
  }

  function handleStatusUpdated(e) {
    var detail = (e && e.detail) || {};
    var closestShop = detail.closestShop || null;
    var closestShopId = closestShop && closestShop.id;

    // Cards
    var urgencyEls = document.querySelectorAll('[data-flavor-urgency]');
    if (urgencyEls.length) {
      // Resolve closest slug once (any urgency el will do — they all share shop map).
      var sample = shopStatesOn(urgencyEls[0]);
      var closestSlug = findSlugByShopId(sample, closestShopId);
      for (var i = 0; i < urgencyEls.length; i++) {
        var scoped = urgencyScopeSlug(urgencyEls[i]);
        if (scoped) {
          // An explicit shop context outranks proximity re-ranking.
          applySelectedToEl(urgencyEls[i], scoped);
        } else {
          applyToUrgencyEl(urgencyEls[i], closestSlug);
        }
      }
    }

    // PDP panels
    var panels = document.querySelectorAll('[data-flavor-availability]');
    for (var j = 0; j < panels.length; j++) {
      applyToPdpPanel(panels[j], closestShopId);
    }
  }

  function handleShopSelected(e) {
    var slug = e && e.detail && e.detail.slug;
    selectedSlug = slug || null;
    var urgencyEls = document.querySelectorAll('[data-flavor-urgency]');
    for (var i = 0; i < urgencyEls.length; i++) {
      if (selectedSlug) {
        applySelectedToEl(urgencyEls[i], selectedSlug);
      } else {
        applyToUrgencyEl(urgencyEls[i], null);
      }
    }
  }

  document.addEventListener('whero:status-updated', handleStatusUpdated);
  document.addEventListener('whero:shop-selected', handleShopSelected);

  // Fixed-shop surfaces (location pages) need no event to scope their
  // cards — apply the data-urgency-shop context once at boot. (This
  // script is deferred, so the DOM is fully parsed here.)
  (function bootFixedScopes() {
    var urgencyEls = document.querySelectorAll('[data-flavor-urgency]');
    for (var i = 0; i < urgencyEls.length; i++) {
      var scoped = urgencyScopeSlug(urgencyEls[i]);
      if (scoped) applySelectedToEl(urgencyEls[i], scoped);
    }
  })();
})();
